/**
 * MultiOrderPusherService
 *
 * Manages multiple simultaneous Pusher subscriptions for FleetHub live tracking.
 * Each active order gets its own Pusher channel subscription using its trackingToken.
 *
 * @module services/pusher/multi-order-pusher-service
 */

import { PusherService, CustomerPusherEventHandlers } from "./pusher-service"

/**
 * Active order subscription tracking
 */
interface OrderSubscription {
  orderId: string
  trackingToken: string
  pusherService: PusherService
  retryCount: number
  lastError?: string
  retryTimeoutId?: NodeJS.Timeout
}

/**
 * MultiOrderPusherService
 *
 * Manages N Pusher connections for concurrent order tracking
 */
export class MultiOrderPusherService {
  private subscriptions: Map<string, OrderSubscription> = new Map()
  private readonly MAX_RETRY_ATTEMPTS = 3
  private readonly RETRY_DELAY_MS = 2000

  /**
   * Subscribe to an order's Pusher channel with error handling and retry logic
   *
   * @param orderId - The order ID
   * @param trackingToken - The order's tracking token for authentication
   * @param handlers - Event handlers for Pusher events
   */
  subscribeToOrder(
    orderId: string,
    trackingToken: string,
    handlers: CustomerPusherEventHandlers,
  ): void {
    // Validate inputs
    if (!orderId || !trackingToken) {
      console.error(`[MultiOrderPusher] Invalid subscription parameters for order ${orderId}`)
      return
    }

    // Check if already subscribed
    if (this.subscriptions.has(orderId)) {
      console.warn(`[MultiOrderPusher] Already subscribed to order ${orderId}`)
      return
    }

    console.log(`[MultiOrderPusher] Subscribing to order ${orderId}`)

    try {
      // Create new Pusher service for this order
      const pusherService = new PusherService(trackingToken)

      // Wrap handlers with error handling
      const safeHandlers: CustomerPusherEventHandlers = {
        onLocationUpdate: (data) => {
          try {
            handlers.onLocationUpdate?.(data)
          } catch (error) {
            console.error(
              `[MultiOrderPusher] Error in onLocationUpdate for order ${orderId}:`,
              error,
            )
          }
        },
        onStageUpdate: (data) => {
          try {
            handlers.onStageUpdate?.(data)
          } catch (error) {
            console.error(`[MultiOrderPusher] Error in onStageUpdate for order ${orderId}:`, error)
          }
        },
        onDriverStarted: (data) => {
          try {
            handlers.onDriverStarted?.(data)
          } catch (error) {
            console.error(
              `[MultiOrderPusher] Error in onDriverStarted for order ${orderId}:`,
              error,
            )
          }
        },
        onDriverArrived: (data) => {
          try {
            handlers.onDriverArrived?.(data)
          } catch (error) {
            console.error(
              `[MultiOrderPusher] Error in onDriverArrived for order ${orderId}:`,
              error,
            )
          }
        },
        onDriverCancelled: (data) => {
          try {
            handlers.onDriverCancelled?.(data)
          } catch (error) {
            console.error(
              `[MultiOrderPusher] Error in onDriverCancelled for order ${orderId}:`,
              error,
            )
          }
        },
        onNumberPlateUpdate: (data) => {
          try {
            handlers.onNumberPlateUpdate?.(data)
          } catch (error) {
            console.error(
              `[MultiOrderPusher] Error in onNumberPlateUpdate for order ${orderId}:`,
              error,
            )
          }
        },
        onETAUpdate: (data) => {
          try {
            handlers.onETAUpdate?.(data)
          } catch (error) {
            console.error(`[MultiOrderPusher] Error in onETAUpdate for order ${orderId}:`, error)
          }
        },
        onOrderCreated: (data) => {
          try {
            handlers.onOrderCreated?.(data)
          } catch (error) {
            console.error(`[MultiOrderPusher] Error in onOrderCreated for order ${orderId}:`, error)
          }
        },
        onPickupRealized: (data) => {
          try {
            handlers.onPickupRealized?.(data)
          } catch (error) {
            console.error(
              `[MultiOrderPusher] Error in onPickupRealized for order ${orderId}:`,
              error,
            )
          }
        },
        onReportAvailable: (data) => {
          try {
            handlers.onReportAvailable?.(data)
          } catch (error) {
            console.error(
              `[MultiOrderPusher] Error in onReportAvailable for order ${orderId}:`,
              error,
            )
          }
        },
      }

      // Subscribe to the order's channel
      pusherService.subscribeToCustomerOrder(orderId, safeHandlers)

      // Store subscription
      this.subscriptions.set(orderId, {
        orderId,
        trackingToken,
        pusherService,
        retryCount: 0,
      })

      console.log(`[MultiOrderPusher] Successfully subscribed to order ${orderId}`)
    } catch (error) {
      console.error(`[MultiOrderPusher] Failed to subscribe to order ${orderId}:`, error)

      // Store failed subscription attempt
      this.subscriptions.set(orderId, {
        orderId,
        trackingToken,
        pusherService: null as any, // Placeholder for failed subscription
        retryCount: 0,
        lastError: error instanceof Error ? error.message : "Unknown error",
      })

      // Attempt retry if within limits
      this.scheduleRetry(orderId, trackingToken, handlers)
    }
  }

  /**
   * Schedule a retry for a failed subscription
   *
   * @param orderId - The order ID
   * @param trackingToken - The tracking token
   * @param handlers - Event handlers
   */
  private scheduleRetry(
    orderId: string,
    trackingToken: string,
    handlers: CustomerPusherEventHandlers,
  ): void {
    const subscription = this.subscriptions.get(orderId)

    if (!subscription || subscription.retryCount >= this.MAX_RETRY_ATTEMPTS) {
      console.error(
        `[MultiOrderPusher] Max retry attempts reached for order ${orderId}. Giving up.`,
      )
      return
    }

    const retryCount = subscription.retryCount + 1
    console.log(
      `[MultiOrderPusher] Scheduling retry ${retryCount}/${this.MAX_RETRY_ATTEMPTS} for order ${orderId} in ${this.RETRY_DELAY_MS}ms`,
    )

    const timeoutId = setTimeout(() => {
      // Remove failed subscription before retrying
      this.subscriptions.delete(orderId)

      // Update retry count
      this.subscribeToOrder(orderId, trackingToken, handlers)

      // Update retry count on the new subscription
      const newSubscription = this.subscriptions.get(orderId)
      if (newSubscription) {
        newSubscription.retryCount = retryCount
      }
    }, this.RETRY_DELAY_MS * retryCount) // Exponential backoff

    // Store timeout ID so it can be cleared during cleanup
    subscription.retryTimeoutId = timeoutId
  }

  /**
   * Unsubscribe from an order's Pusher channel with error handling
   *
   * @param orderId - The order ID to unsubscribe from
   */
  unsubscribeFromOrder(orderId: string): void {
    const subscription = this.subscriptions.get(orderId)

    if (!subscription) {
      console.warn(`[MultiOrderPusher] No subscription found for order ${orderId}`)
      return
    }

    console.log(`[MultiOrderPusher] Unsubscribing from order ${orderId}`)

    try {
      // Clear pending retry timeout if it exists
      if (subscription.retryTimeoutId) {
        clearTimeout(subscription.retryTimeoutId)
      }

      // Disconnect Pusher service if it exists
      if (subscription.pusherService) {
        subscription.pusherService.disconnect()
      }
    } catch (error) {
      console.error(`[MultiOrderPusher] Error disconnecting order ${orderId}:`, error)
    } finally {
      // Always remove from subscriptions map
      this.subscriptions.delete(orderId)
    }
  }

  /**
   * Unsubscribe from all orders and disconnect all Pusher connections
   * Handles errors gracefully to ensure cleanup completes
   */
  unsubscribeAll(): void {
    console.log(`[MultiOrderPusher] Unsubscribing from all ${this.subscriptions.size} orders`)

    const errors: Array<{ orderId: string; error: any }> = []

    // Disconnect each subscription with error handling
    this.subscriptions.forEach((subscription, orderId) => {
      try {
        // Clear pending retry timeout if it exists
        if (subscription.retryTimeoutId) {
          clearTimeout(subscription.retryTimeoutId)
        }

        if (subscription.pusherService) {
          subscription.pusherService.disconnect()
        }
      } catch (error) {
        console.error(`[MultiOrderPusher] Error disconnecting order ${orderId}:`, error)
        errors.push({ orderId, error })
      }
    })

    // Always clear subscriptions map
    this.subscriptions.clear()

    if (errors.length > 0) {
      console.warn(
        `[MultiOrderPusher] Completed unsubscribeAll with ${errors.length} errors:`,
        errors,
      )
    } else {
      console.log(`[MultiOrderPusher] Successfully unsubscribed from all orders`)
    }
  }

  /**
   * Get list of currently active order subscriptions
   *
   * @returns Array of order IDs currently subscribed
   */
  getActiveSubscriptions(): string[] {
    return Array.from(this.subscriptions.keys())
  }

  /**
   * Check if an order is currently subscribed
   *
   * @param orderId - The order ID to check
   * @returns True if subscribed, false otherwise
   */
  isSubscribed(orderId: string): boolean {
    return this.subscriptions.has(orderId)
  }

  /**
   * Get connection state for a specific order
   *
   * @param orderId - The order ID
   * @returns Connection state or null if not subscribed
   */
  getOrderConnectionState(orderId: string): { isConnected: boolean; state: string } | null {
    const subscription = this.subscriptions.get(orderId)

    if (!subscription) {
      return null
    }

    return subscription.pusherService.getConnectionState()
  }

  /**
   * Get total number of active subscriptions
   *
   * @returns Count of active subscriptions
   */
  getSubscriptionCount(): number {
    return this.subscriptions.size
  }

  /**
   * Get all failed subscriptions with error information
   *
   * @returns Array of failed subscriptions
   */
  getFailedSubscriptions(): Array<{ orderId: string; error: string }> {
    const failed: Array<{ orderId: string; error: string }> = []

    this.subscriptions.forEach((subscription) => {
      if (subscription.lastError) {
        failed.push({
          orderId: subscription.orderId,
          error: subscription.lastError,
        })
      }
    })

    return failed
  }

  /**
   * Check if any subscriptions have failed
   *
   * @returns True if any subscriptions have errors
   */
  hasFailedSubscriptions(): boolean {
    return Array.from(this.subscriptions.values()).some((sub) => sub.lastError !== undefined)
  }
}
